"use client";

import {
  Calendar,
  Clock,
  Flag,
  Link2,
  MapPin,
  Quote,
  Send,
  Share2,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useReducer, useRef, useState } from "react";
import { toast } from "sonner";
import type { SocialFeedItem } from "@/actions/social-feed";
import { FeedActionRow } from "@/components/feed/feed-action-row";
import {
  FEED_X_BODY,
  FeedCardHeader,
} from "@/components/feed/feed-card-header";
import { RegularPostDrawer } from "@/components/feed/regular-post-drawer";
import { ReportFeedItemDialog } from "@/components/feed/report-feed-item-dialog";
import {
  CommentsSheet,
  type CommentsSheetViewer,
} from "@/components/posts/comments-sheet";
import { LikesSheet } from "@/components/posts/likes-sheet";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { PosterVideo } from "@/components/ui/poster-video";
import {
  useDeleteFeedItemMutation,
  useTrackFeedShareMutation,
} from "@/hooks/use-feed-share";
import { useToggleSocialFeedLikeMutation } from "@/hooks/use-social-feed-interactions";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";
import { formatRelativeTimeCompact } from "@/lib/dates/relative-time";
import { formatEventAttendanceSchedule } from "@/lib/feed/event-attendance-schedule";
import type { FeedEntityQuoteTarget } from "@/lib/feed/feed-entity-quote";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import { toRenderableMedia } from "@/lib/media";
import { absolutePublicAppUrl } from "@/lib/public-app-url";
import { cn } from "@/lib/utils";

type SocialFeedItemProps = {
  item: SocialFeedItem;
  viewer?: CommentsSheetViewer;
  embedded?: boolean;
};

type InteractionState = {
  sourceKey: string;
  likeCount: number;
  isLiked: boolean;
  commentCount: number;
  shareCount: number;
};

type InteractionAction =
  | { type: "sync"; state: InteractionState }
  | { type: "toggleLike" }
  | { type: "restoreLike"; isLiked: boolean; likeCount: number }
  | { type: "setCommentCount"; commentCount: number }
  | { type: "setShareCount"; shareCount: number };

const getInteractionSourceKey = (item: SocialFeedItem) =>
  [
    item.entityType,
    item.entityId,
    item.likeCount,
    item.isLikedByViewer,
    item.commentCount,
    item.shareCount,
  ].join(":");

const getInteractionState = (item: SocialFeedItem): InteractionState => ({
  sourceKey: getInteractionSourceKey(item),
  likeCount: item.likeCount,
  isLiked: item.isLikedByViewer,
  commentCount: item.commentCount,
  shareCount: item.shareCount,
});

const interactionReducer = (
  state: InteractionState,
  action: InteractionAction,
): InteractionState => {
  switch (action.type) {
    case "sync":
      return action.state;
    case "toggleLike":
      return {
        ...state,
        isLiked: !state.isLiked,
        likeCount: state.likeCount + (state.isLiked ? -1 : 1),
      };
    case "restoreLike":
      return {
        ...state,
        isLiked: action.isLiked,
        likeCount: action.likeCount,
      };
    case "setCommentCount":
      return {
        ...state,
        commentCount: action.commentCount,
      };
    case "setShareCount":
      return {
        ...state,
        shareCount: action.shareCount,
      };
  }
};

export function SocialFeedItemCard({
  item,
  viewer,
  embedded = false,
}: SocialFeedItemProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCommentsOpen, setCommentsOpen] = useState(false);
  const [isLikesOpen, setLikesOpen] = useState(false);
  const [isActionsOpen, setActionsOpen] = useState(false);
  const [isReportOpen, setReportOpen] = useState(false);
  const [isQuoteOpen, setQuoteOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isSendInChatOpen, setSendInChatOpen] = useState(false);
  const [interactionState, dispatchInteraction] = useReducer(
    interactionReducer,
    item,
    getInteractionState,
  );

  const toggleLikeMutation = useToggleSocialFeedLikeMutation();
  const trackShareMutation = useTrackFeedShareMutation();
  const deleteFeedItemMutation = useDeleteFeedItemMutation();
  const interactionSourceKey = getInteractionSourceKey(item);

  if (interactionState.sourceKey !== interactionSourceKey) {
    dispatchInteraction({
      type: "sync",
      state: getInteractionState(item),
    });
  }

  const { commentCount, isLiked, likeCount, shareCount } = interactionState;

  const createdAtDate = new Date(item.createdAt);
  const timeAgo = formatRelativeTimeCompact(createdAtDate);

  const actorHandle = item.actor.username;
  const actorName = item.actor.displayName || item.actor.username || "User";
  const isOwner = Boolean(viewer?.id && viewer.id === item.actor.id);

  const bannerMedia =
    item.type === "hiive_joined"
      ? item.hiive.bannerMedia
      : (item.event?.bannerMedia ?? null);

  const renderableBanner = bannerMedia ? toRenderableMedia(bannerMedia) : null;
  const isVideo = renderableBanner ? renderableBanner.kind === "video" : false;
  const transformedUrl = renderableBanner?.url ?? null;

  const actionInfo = (() => {
    if (item.type === "hiive_joined") {
      return {
        verb: "joined",
        targetName: item.hiive.name,
        targetHref: `/hiives/${item.hiive.slug}`,
        startTime: null as Date | null,
        endTime: null as Date | null,
        hiiveName: null as string | null,
        hiiveLogoUrl: item.hiive.logoUrl,
        isEvent: false,
      };
    }

    const verb = item.type === "event_attending" ? "is attending" : "attended";
    return {
      verb,
      targetName: item.event.name,
      targetHref: `/hiives/${item.hiive.slug}/events/${item.event.slug}`,
      startTime: item.occurrence.startTime as Date,
      endTime: item.occurrence.endTime as Date,
      hiiveName: item.hiive.name,
      hiiveLogoUrl: item.hiive.logoUrl,
      isEvent: true,
    };
  })();
  const quoteTarget = {
    entityType: item.entityType,
    entityId: item.entityId,
    item,
  } satisfies FeedEntityQuoteTarget;

  const scheduleLabel =
    actionInfo.startTime && actionInfo.endTime
      ? formatEventAttendanceSchedule(actionInfo.startTime, actionInfo.endTime)
      : null;

  // Intersection observer for autoplay
  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !isVideo || !video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          void video.play().catch(() => {});
          return;
        }

        video.pause();
      },
      { threshold: 0.5 },
    );

    observer.observe(container);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [isVideo]);

  const handleToggleLike = () => {
    const previousLiked = isLiked;
    const previousCount = likeCount;

    dispatchInteraction({ type: "toggleLike" });

    toggleLikeMutation.mutate(
      {
        entityType: item.entityType,
        entityId: item.entityId,
        currentlyLiked: previousLiked,
      },
      {
        onError: () => {
          dispatchInteraction({
            type: "restoreLike",
            isLiked: previousLiked,
            likeCount: previousCount,
          });
          toast.error("Couldn't update like right now");
        },
      },
    );
  };

  const handleCommentCountChange = (count: number) => {
    dispatchInteraction({ type: "setCommentCount", commentCount: count });
  };

  const getShareUrl = () => absolutePublicAppUrl(actionInfo.targetHref);

  const recordShare = (
    target: "system" | "copy" | "chat" | "native" | "story",
  ) => {
    trackShareMutation.mutate(
      { entityType: item.entityType, entityId: item.entityId, target },
      {
        onSuccess: (result) => {
          dispatchInteraction({
            type: "setShareCount",
            shareCount: result.shareCount,
          });
        },
      },
    );
  };

  const handleOpenQuote = () => {
    setActionsOpen(false);
    setQuoteOpen(true);
  };

  const handleOpenSendInChat = () => {
    setActionsOpen(false);
    setSendInChatOpen(true);
  };

  const handleOpenDeleteConfirm = () => {
    setActionsOpen(false);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteFromFeed = () => {
    deleteFeedItemMutation.mutate(
      { entityType: item.entityType, entityId: item.entityId },
      {
        onSuccess: () => {
          toast.success("Removed from feed");
          setDeleteConfirmOpen(false);
        },
        onError: (error) => {
          toast.error(
            error instanceof Error
              ? error.message
              : "Couldn't remove this feed item",
          );
        },
      },
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      recordShare("copy");
      toast.success("Link copied");
      setActionsOpen(false);
    } catch (_error) {
      toast.error("Couldn't copy link");
    }
  };

  const handleShare = () => {
    setActionsOpen(false);
    setSendInChatOpen(true);
  };

  return (
    <article
      ref={containerRef}
      className={cn(
        "border-b border-border bg-background",
        embedded && "border-b-0",
      )}
    >
      <FeedCardHeader
        layout={embedded ? "row" : "x"}
        displayName={actorName}
        handle={actorHandle}
        avatarUrl={item.actor.avatarUrl}
        createdAt={createdAtDate}
        timeAgo={timeAgo}
      />
      <div className={embedded ? undefined : FEED_X_BODY}>
        {/* Auto-generated stories carry no caption line: the header (author +
          time) and the card (action verb + target) already say everything. */}

        {/* Compact ambient card — banner as blurred backdrop */}
        <div className={cn("px-3", embedded && "pb-3")}>
          <BackAwareLink
            href={actionInfo.targetHref}
            className="relative block overflow-hidden rounded-2xl border border-border bg-surface shadow-sm dark:border-glass-border-10 dark:bg-transparent dark:shadow-none"
          >
            {/* Blurred banner backdrop — use poster for video, image otherwise */}
            {renderableBanner && transformedUrl ? (
              <div className="absolute inset-0 scale-110">
                {isVideo && renderableBanner.poster?.url ? (
                  <Image
                    src={renderableBanner.poster.url}
                    alt=""
                    fill
                    className="scale-110 object-cover blur-md"
                    sizes="(max-width: 768px) 100vw, 600px"
                  />
                ) : !isVideo ? (
                  <Image
                    src={transformedUrl}
                    alt=""
                    fill
                    className="scale-110 object-cover blur-md"
                    sizes="(max-width: 768px) 100vw, 600px"
                  />
                ) : null}
              </div>
            ) : null}

            {/* Ambient overlay */}
            <div
              className={cn(
                "absolute inset-0",
                renderableBanner
                  ? "bg-surface/84 dark:bg-black/55"
                  : "bg-surface-alt dark:bg-gradient-to-br dark:from-brand/25 dark:via-brand/15 dark:to-purple-500/25",
              )}
            />

            {/* Card content. Sized for the post column (~300px on a phone):
                nothing is absolutely positioned over the text — the hiive
                logo rides inline with the hiive's name instead of pinning a
                corner the title then runs under. */}
            <div className="relative flex items-center gap-3 p-2.5">
              {/* Left: 4:5 banner thumbnail */}
              <div
                className={`relative w-[72px] flex-shrink-0 overflow-hidden rounded-xl bg-surface-alt shadow-sm ring-1 ring-border dark:bg-glass-bg-10 dark:shadow-lg dark:ring-white/10 ${ASPECT_RATIO_CLASS.portrait4By5}`}
              >
                {renderableBanner && transformedUrl ? (
                  isVideo ? (
                    <PosterVideo
                      ref={videoRef}
                      src={transformedUrl}
                      mimeType={renderableBanner.mimeType}
                      muted
                      playsInline
                      loop
                      posterUrl={renderableBanner.poster?.url}
                      blurHash={renderableBanner.blurHash}
                      posterSizes="72px"
                      preload="metadata"
                    />
                  ) : (
                    <Image
                      src={transformedUrl}
                      alt={actionInfo.targetName}
                      fill
                      className="object-cover"
                      sizes="72px"
                    />
                  )
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    {actionInfo.isEvent ? (
                      <Calendar className="size-6 text-text-tertiary dark:text-white/40" />
                    ) : (
                      <Users className="size-6 text-text-tertiary dark:text-white/40" />
                    )}
                  </div>
                )}
              </div>

              {/* Right: verb eyebrow → title → where/when */}
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[11px] font-semibold tracking-[0.08em] text-brand uppercase dark:text-violet-300">
                  {actionInfo.verb === "joined"
                    ? "Joined"
                    : actionInfo.verb === "is attending"
                      ? "Attending"
                      : "Attended"}
                </span>

                <h3 className="mt-0.5 line-clamp-2 text-[15px] leading-[1.25] font-semibold text-text-primary dark:text-white">
                  {actionInfo.targetName}
                </h3>

                {/* Who it belongs to: the hiive logo inline with its name (an
                    event), or with the hiive's own location (a join). */}
                {actionInfo.hiiveLogoUrl ||
                actionInfo.hiiveName ||
                (item.type === "hiive_joined" && item.hiive.location) ? (
                  <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[12px] text-text-secondary dark:text-white/65">
                    {actionInfo.hiiveLogoUrl ? (
                      <span className="relative size-4 shrink-0 overflow-hidden rounded-[5px] bg-surface ring-1 ring-border dark:bg-black/40 dark:ring-white/15">
                        <Image
                          src={actionInfo.hiiveLogoUrl}
                          alt=""
                          fill
                          className="object-contain"
                          sizes="16px"
                        />
                      </span>
                    ) : item.type === "hiive_joined" ? (
                      <MapPin className="size-3.5 shrink-0" />
                    ) : null}
                    <span className="truncate">
                      {actionInfo.hiiveName ??
                        (item.type === "hiive_joined"
                          ? item.hiive.location
                          : null)}
                    </span>
                  </div>
                ) : null}

                {scheduleLabel ? (
                  <div className="mt-0.5 flex min-w-0 items-start gap-1.5 text-[12px] leading-4 text-text-secondary dark:text-white/65">
                    <Clock className="mt-px size-3.5 shrink-0" />
                    {/* Wraps rather than cutting the end time. */}
                    <span className="line-clamp-2">{scheduleLabel}</span>
                  </div>
                ) : null}

                {/* Joined: the hiive's activities, one row, never wrapping. */}
                {item.type === "hiive_joined" &&
                item.hiive.activities.length > 0 ? (
                  <div className="mt-1.5 flex min-w-0 items-center gap-1 overflow-hidden">
                    {item.hiive.activities.slice(0, 2).map((act) => {
                      const Icon = getIconComponent(act.iconKey as IconType);
                      return (
                        <span
                          key={act.id}
                          className="inline-flex min-w-0 shrink items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 backdrop-blur-sm dark:bg-glass-bg-10"
                        >
                          {Icon && (
                            <Icon className="size-3 shrink-0 text-text-tertiary dark:text-white/60" />
                          )}
                          <span className="truncate text-[11px] font-medium text-text-secondary dark:text-white/75">
                            {act.name}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </BackAwareLink>
        </div>

        {!embedded ? (
          <FeedActionRow
            layout="x"
            className="px-3"
            likeCount={likeCount}
            commentCount={commentCount}
            shareCount={shareCount}
            isLiked={isLiked}
            onLike={handleToggleLike}
            onOpenLikes={() => setLikesOpen(true)}
            onComment={() => setCommentsOpen(true)}
            onQuote={handleOpenQuote}
            onShare={handleShare}
            onMore={() => setActionsOpen(true)}
            likeDisabled={toggleLikeMutation.isPending}
            shareDisabled={trackShareMutation.isPending}
          />
        ) : null}

        <Drawer open={isActionsOpen} onOpenChange={setActionsOpen} modal>
          <DrawerContent className="border-t border-border outline-none">
            <DrawerTitle className="sr-only">Feed item actions</DrawerTitle>
            <div className="mx-auto w-full max-w-lg p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleShare}
                >
                  <Share2 className="h-5 w-5" />
                  Share
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleOpenSendInChat}
                >
                  <Send className="h-5 w-5" />
                  Send in chat
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleCopyLink}
                >
                  <Link2 className="h-5 w-5" />
                  Copy link
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleOpenQuote}
                >
                  <Quote className="h-5 w-5" />
                  Quote repost
                </Button>
                {!isOwner ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                    onClick={() => {
                      setActionsOpen(false);
                      setReportOpen(true);
                    }}
                  >
                    <Flag className="h-5 w-5" />
                    Report
                  </Button>
                ) : null}
                {isOwner ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-destructive"
                    onClick={handleOpenDeleteConfirm}
                    disabled={deleteFeedItemMutation.isPending}
                  >
                    <Trash2 className="h-5 w-5" />
                    Delete from feed
                  </Button>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                className="mt-3 h-12 w-full rounded-xl"
                onClick={() => setActionsOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </DrawerContent>
        </Drawer>

        <CommentsSheet
          entityType="social"
          entityId={item.entityId}
          socialEntityType={item.entityType}
          open={isCommentsOpen}
          onOpenChange={setCommentsOpen}
          viewer={viewer}
          canModerate={isOwner}
          onCommentCountChange={handleCommentCountChange}
        />

        <LikesSheet
          entityType="social"
          entityId={item.entityId}
          socialEntityType={item.entityType}
          open={isLikesOpen}
          onOpenChange={setLikesOpen}
          currentUserId={viewer?.id}
        />

        {!isOwner ? (
          <ReportFeedItemDialog
            open={isReportOpen}
            onOpenChange={setReportOpen}
            targetUserId={item.actor.id}
            targetLabel={
              item.type === "hiive_joined" ? "hiive update" : "event attendance"
            }
            entityType={item.entityType}
            entityId={item.entityId}
          />
        ) : null}
        {isQuoteOpen ? (
          <RegularPostDrawer
            open={isQuoteOpen}
            onOpenChange={setQuoteOpen}
            currentUser={{
              id: viewer?.id ?? "",
              displayName: viewer?.displayName ?? "You",
              avatarUrl: viewer?.avatarUrl ?? null,
            }}
            quoteTarget={{
              kind: "feed_entity",
              target: quoteTarget,
              defaultVisibility: "private",
            }}
            onSuccess={() => setQuoteOpen(false)}
          />
        ) : null}
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete from feed?</DialogTitle>
              <DialogDescription>
                This removes the item from your feed without deleting the
                underlying event or membership record.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteFeedItemMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteFromFeed}
                disabled={deleteFeedItemMutation.isPending}
              >
                {deleteFeedItemMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <SendToChatDrawer
          open={isSendInChatOpen}
          onOpenChange={setSendInChatOpen}
          payload={{
            type: "entity",
            entityType: item.entityType,
            entityId: String(item.entityId),
          }}
          nativeShare={{
            title: actionInfo.targetName,
            text:
              item.type === "hiive_joined"
                ? `${actorName} joined ${actionInfo.targetName} on Hiive`
                : `${actorName} ${actionInfo.verb} ${actionInfo.targetName}`,
            url: getShareUrl(),
          }}
          onShared={recordShare}
        />
      </div>
    </article>
  );
}
